"use client";
import { z } from "zod";
import { MAX_WORKSPACE_PERSISTENCE_BYTES, parseWorkspacePersistenceResult, workspaceLayoutSchema, workspacePersistenceRequestSchema,
  workspaceSnapshotSchema, workspaceRecoverySaveGuardSchema, type WorkspaceRecoverySaveGuard, type WorkspacePersistenceRequest, type WorkspacePersistenceResult } from "../workspacePersistence";
import { WorkspaceOwnerChangedError, WorkspaceRequestRejectedError } from "./workspacePersistenceErrors";

export type WorkspaceSnapshot = z.infer<typeof workspaceSnapshotSchema>;
export type WorkspaceLayout = z.infer<typeof workspaceLayoutSchema>;
type Payload = NonNullable<WorkspaceSnapshot["components"][number]["payload"]>;
type ComponentSnapshot = WorkspaceSnapshot["components"][number];
type ComponentEffectBarrier = {
  kind: "component";
  componentId: string; before: Payload; predecessors: EffectBarrier[]; base?: Payload; locallyChanged: boolean; layoutChanged: boolean; result?: ComponentSnapshot;
  execute: (expectedFingerprint: string) => Promise<ComponentSnapshot>;
  reconcile: (saved: ComponentSnapshot, before: Payload) => boolean;
  expectedFingerprint?: string;
  needsResume?: boolean;
  reconciliationConflict?: WorkspaceSaveConflictError;
  recovering?: boolean;
};
type WorkspaceEffectBarrier = {
  kind: "workspace"; before: WorkspaceSaveDraft; predecessors: EffectBarrier[]; base?: WorkspaceSaveDraft;
  expected?: WorkspaceSnapshot; result?: WorkspaceSnapshot;
  execute: (expected: WorkspaceSnapshot) => Promise<WorkspaceSnapshot>;
  reconcile: (saved: WorkspaceSnapshot, before: WorkspaceSaveDraft) => boolean;
  needsResume?: boolean; reconciliationConflict?: WorkspaceSaveConflictError;
};
type EffectBarrier = ComponentEffectBarrier | WorkspaceEffectBarrier;
type SaveRequest = Extract<WorkspacePersistenceRequest, { mode: "save" }>;
export type WorkspaceTransport = (request: WorkspacePersistenceRequest) => Promise<WorkspacePersistenceResult>;
export type WorkspaceSaveDraft = { components: Payload[]; layout?: WorkspaceLayout };
const draftSchema = z.object({
  components: z.array(z.object({ id: z.string().min(1).max(256).refine(id => id.trim() === id) }).passthrough())
    .max(1000).refine(values => new Set(values.map(value => value.id)).size === values.length),
  layout: workspaceLayoutSchema.optional(),
}).strict();

export class WorkspaceSaveConflictError extends Error {
  constructor(readonly componentId: string | null) {
    super("This workspace changed in another window. Reload the saved workspace before saving again.");
    this.name = "WorkspaceSaveConflictError";
  }
}

export class WorkspaceRecoveryPausedError extends WorkspaceSaveConflictError {
  constructor() { super(null); this.message = "Workspace recovery is being reviewed"; this.name = "WorkspaceRecoveryPausedError"; }
}

// Compare JSON persistence values, ignoring object insertion order. Clone on
// entry so later mutations of store objects cannot change a queued request.
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, item) => item && typeof item === "object" && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
}
const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const sameClosedContent = (left: Payload, right: Payload | undefined) => right !== undefined
  && canonical({ ...left, deletedDate: undefined }) === canonical({ ...right, deletedDate: undefined });

/** Merge common ordering and preserve the anchors around independent additions. */
function mergeWorkspaceReferenceOrder(previous: string[], remote: string[], local: string[], retained: ReadonlySet<string>): string[] {
  const base = previous.filter(id => retained.has(id));
  const baseIds = new Set(base);
  const remoteBase = remote.filter(id => baseIds.has(id)), localBase = local.filter(id => baseIds.has(id));
  let order: string[];
  if (canonical(localBase) === canonical(base) || canonical(localBase) === canonical(remoteBase)) order = remoteBase;
  else if (canonical(remoteBase) === canonical(base)) order = localBase;
  else throw new WorkspaceSaveConflictError(null);
  const priority = Array.from(new Set([...remote, ...local, ...previous])).filter(id => retained.has(id));
  const edges = new Map(priority.map(id => [id, new Set<string>()]));
  const incoming = new Map(priority.map(id => [id, 0]));
  const connect = (left: string, right: string) => {
    if (left === right || edges.get(left)!.has(right)) return;
    edges.get(left)!.add(right); incoming.set(right, incoming.get(right)! + 1);
  };
  for (let index = 1; index < order.length; index++) connect(order[index - 1], order[index]);
  for (const sequence of [remote, local]) {
    const visible = sequence.filter(id => retained.has(id));
    for (let index = 1; index < visible.length; index++) {
      const left = visible[index - 1], right = visible[index];
      if (!baseIds.has(left) || !baseIds.has(right)) connect(left, right);
    }
  }
  const result: string[] = [], visited = new Set<string>();
  while (result.length < priority.length) {
    const id = priority.find(candidate => !visited.has(candidate) && incoming.get(candidate) === 0);
    if (id === undefined) throw new WorkspaceSaveConflictError(null);
    result.push(id); visited.add(id);
    for (const next of Array.from(edges.get(id)!)) incoming.set(next, incoming.get(next)! - 1);
  }
  return result;
}

/** Apply only changes to rows and layout fields that a later draft left alone.
 * Conflicting edits stay in the queue instead of acquiring newer row tokens. */
export function reconcileWorkspaceEffectDraft(before: WorkspaceSaveDraft, saved: WorkspaceSnapshot, draft: WorkspaceSaveDraft): WorkspaceSaveDraft {
  if (!before.layout) throw new Error("Workspace effects require a captured layout");
  const raw = saved.layout.payload;
  const layout = workspaceLayoutSchema.parse({ ...raw, componentsMap: typeof raw.componentsMap === "string" ? JSON.parse(raw.componentsMap) : raw.componentsMap });
  const beforeRows = new Map(before.components.map(row => [row.id, row]));
  const savedRows = new Map(saved.components.map(row => [row.componentId, row]));
  const beforeRefs = new Map(before.layout.componentsMap.map(ref => [ref.id, ref]));
  const savedRefs = new Map(layout.componentsMap.map(ref => [ref.id, ref]));
  for (const ref of layout.componentsMap) if (!savedRows.get(ref.id)?.payload) throw new Error("Workspace effect omitted an active row");
  const choose = <T>(previous: T | undefined, next: T | undefined, local: T | undefined, componentId: string | null): T | undefined => {
    if (canonical(local) === canonical(previous) || canonical(local) === canonical(next)) return next;
    if (canonical(next) === canonical(previous)) return local;
    throw new WorkspaceSaveConflictError(componentId);
  };
  const components: Payload[] = [];
  for (const local of draft.components) {
    const previous = beforeRows.get(local.id);
    const remote = savedRows.get(local.id);
    if (beforeRefs.has(local.id) && !savedRefs.has(local.id)) {
      const locallyClosed = draft.layout && !draft.layout.componentsMap.some(ref => ref.id === local.id);
      if (canonical(local) !== canonical(previous) && !(locallyClosed && sameClosedContent(local, previous))) throw new WorkspaceSaveConflictError(local.id);
      continue;
    }
    // Rows outside the transaction's read set keep their original authority.
    if (!remote) { components.push(copy(local)); continue; }
    const selected = choose(previous, remote.payload ?? undefined, local, local.id);
    if (selected) components.push(copy(selected));
  }
  if (!draft.layout) return { components };
  const localRefs = new Map(draft.layout.componentsMap.map(ref => [ref.id, ref]));
  const mergedRefs = Array.from(new Set([...Array.from(localRefs.keys()), ...Array.from(savedRefs.keys()), ...Array.from(beforeRefs.keys())]))
    .flatMap(id => { const ref = choose(beforeRefs.get(id), savedRefs.get(id), localRefs.get(id), id); return ref ? [copy(ref)] : []; });
  const refsById = new Map(mergedRefs.map(ref => [ref.id, ref]));
  const refs = mergeWorkspaceReferenceOrder(before.layout.componentsMap.map(ref => ref.id), layout.componentsMap.map(ref => ref.id),
    draft.layout.componentsMap.map(ref => ref.id), new Set(refsById.keys())).map(id => refsById.get(id)!);
  // Remote additions are already persisted; include their displayed payloads
  // only when constructing a complete layout draft for reconciliation.
  for (const ref of refs) if (!components.some(row => row.id === ref.id) && !beforeRows.has(ref.id)) {
    const remote = savedRows.get(ref.id)?.payload;
    if (remote) components.push(copy(remote));
  }
  return { components, layout: workspaceLayoutSchema.parse({ componentsMap: refs,
    notificationWindowDismissed: choose(before.layout.notificationWindowDismissed, layout.notificationWindowDismissed, draft.layout.notificationWindowDismissed, null),
    lastLayoutMode: choose(before.layout.lastLayoutMode, layout.lastLayoutMode, draft.layout.lastLayoutMode, null),
  }) };
}

function reconcileEffectDraft(draft: WorkspaceSaveDraft, barriers: EffectBarrier[]): WorkspaceSaveDraft {
  let components = draft.components;
  let layout = draft.layout;
  for (const barrier of barriers) {
    if (barrier.kind === "workspace") {
      if (barrier.result) {
        const next = reconcileWorkspaceEffectDraft(barrier.base ?? barrier.before, barrier.result, { components, ...(layout ? { layout } : {}) });
        components = next.components; layout = next.layout;
      }
      continue;
    }
    if (barrier.result?.payload) components = components.map(value => {
      if (value.id !== barrier.componentId) return value;
      const closed = layout && !layout.componentsMap.some(item => item.id === barrier.componentId);
      if (barrier.recovering && closed && (sameClosedContent(value, barrier.before) || sameClosedContent(value, barrier.base))) {
        return { ...copy(barrier.result!.payload!), ...(value.deletedDate !== undefined ? { deletedDate: value.deletedDate } : {}) };
      }
      return canonical(value) === canonical(barrier.before) || canonical(value) === canonical(barrier.base) ? copy(barrier.result!.payload!) : value;
    });
    else if (barrier.recovering && barrier.result?.payload === null) {
      components = components.filter(value => value.id !== barrier.componentId);
      if (layout) layout = { ...layout, componentsMap: layout.componentsMap.filter(value => value.id !== barrier.componentId) };
    }
  }
  return { components, ...(layout ? { layout } : {}) };
}

/** One authenticated workspace load owns one queue. Never rebase dirty data. */
export class WorkspaceSaveQueue {
  private pendingUncertain = false;
  private external: EffectBarrier | null = null;
  private effectBarriers: EffectBarrier[] = [];
  private retainedSaves: { draft: WorkspaceSaveDraft; preserveMissing: boolean; barriers: EffectBarrier[]; failed: boolean }[] = [];
  private components: Map<string, WorkspaceSnapshot["components"][number]>;
  private layout: WorkspaceSnapshot["layout"];
  private tail: Promise<unknown> = Promise.resolve();
  private pending: SaveRequest | null = null;
  private plan: { draft: WorkspaceSaveDraft; preserveMissing: boolean } | null = null;
  private conflict: WorkspaceSaveConflictError | null = null;
  private invalidated = false;
  private recoveryPause: symbol | null = null;
  private recoverySubmission = 0;
  private recoveryTouchedIds = new Map<string, number>();
  private recoveryValidationPending = false;

  constructor(
    private readonly identity: { expectedOwnerId: string; expectedCompanyId: string },
    snapshots: WorkspaceSnapshot | WorkspaceSnapshot[],
    private readonly transport: WorkspaceTransport,
    private readonly isCurrent: () => boolean,
    private recoveryGuard?: WorkspaceRecoverySaveGuard,
  ) {
    // Reuse the wire verifier even for bootstrap-injected state.
    const pages = Array.isArray(snapshots) ? snapshots : [snapshots];
    if (!pages.length || pages.length > 10) throw new Error("Invalid workspace bootstrap");
    const verified = pages.map(snapshot => {
      const request = workspacePersistenceRequestSchema.parse({ ...identity, mode: "inspect", componentIds: snapshot.components.map(value => value.componentId) });
      const page = parseWorkspacePersistenceResult(request, snapshot);
      if (!("layout" in page)) throw new Error("Invalid workspace bootstrap");
      return page;
    });
    const components = verified.flatMap(page => page.components);
    if (new Set(components.map(value => value.componentId)).size !== components.length
      || verified.some(page => canonical(page.layout) !== canonical(verified[0].layout))) throw new Error("Workspace bootstrap changed between pages");
    this.identity = copy(identity);
    this.recoveryGuard = recoveryGuard ? workspaceRecoverySaveGuardSchema.parse(copy(recoveryGuard)) : undefined;
    this.recoveryValidationPending = Boolean(this.recoveryGuard);
    this.components = new Map(copy(components).map(value => [value.componentId, value]));
    this.layout = copy(verified[0].layout);
  }

  invalidate() { this.invalidated = true; }

  /** The selected recovery save must settle before this becomes a normal queue. */
  completeRecovery() {
    this.assertCurrent();
    if (this.hasPendingWrite() || this.plan || this.recoveryValidationPending) throw new Error("The recovery save has not finished");
    this.recoveryGuard = undefined;
  }

  hasPendingWrite() { return this.recoveryPause !== null || this.conflict !== null || this.pending !== null || this.external !== null || this.effectBarriers.length > 0
    || this.retainedSaves.some(value => value.failed); }

  /** Stop new dispatch, settle work already in flight, and retain failed plans.
   * Releasing a review never retries or discards an operation. */
  async pauseForRecovery(): Promise<() => void> {
    if (this.invalidated || !this.isCurrent()) throw new WorkspaceOwnerChangedError();
    if (this.recoveryPause) throw new WorkspaceRecoveryPausedError();
    const token = Symbol("workspace recovery");
    this.recoveryPause = token;
    try {
      await this.tail;
      if (this.invalidated || !this.isCurrent()) throw new WorkspaceOwnerChangedError();
      return () => { if (this.recoveryPause === token) this.recoveryPause = null; };
    } catch (error) {
      if (this.recoveryPause === token) this.recoveryPause = null;
      throw error;
    }
  }

  /** Include locally closed unsaved views when assembling the owner's review. */
  recoveryComponentIds(): string[] {
    if (this.invalidated || !this.isCurrent()) throw new WorkspaceOwnerChangedError();
    return Array.from(new Set([
      ...Array.from(this.recoveryTouchedIds.keys()),
      ...(this.pending?.changes.map(row => row.componentId) ?? []),
      ...(this.plan?.draft.components.map(row => row.id) ?? []),
      ...this.retainedSaves.flatMap(save => save.draft.components.map(row => row.id)),
    ]));
  }

  /** Serialize a source-scoped external commit without rebasing queued local edits. */
  applyEffect(componentId: string, before: Payload, execute: ComponentEffectBarrier["execute"], reconcile: ComponentEffectBarrier["reconcile"], options: { recovering?: boolean } = {}): Promise<void> {
    const original = draftSchema.parse({ components: [copy(before)] }).components[0];
    if (original.id !== componentId) return Promise.reject(new Error("Component effect target changed"));
    const barrier: ComponentEffectBarrier = { kind: "component", componentId, before: original, predecessors: [...this.effectBarriers], locallyChanged: false, layoutChanged: false, execute, reconcile,
      recovering: options.recovering === true };
    this.effectBarriers.push(barrier);
    const operation = this.tail.then(async () => {
      try {
        this.assertCurrent();
        await this.finishQueued();
      } catch (error) {
        barrier.needsResume = true;
        throw error;
      }
      await this.startEffect(barrier);
    });
    this.tail = operation.catch(() => undefined);
    return operation;
  }

  /** Serialize a transaction that owns multiple rows and the active layout. */
  applyWorkspaceEffect(before: WorkspaceSaveDraft, execute: WorkspaceEffectBarrier["execute"], reconcile: WorkspaceEffectBarrier["reconcile"]): Promise<void> {
    const original = draftSchema.parse(copy(before));
    if (!original.layout) return Promise.reject(new Error("Workspace effects require a captured layout"));
    const barrier: WorkspaceEffectBarrier = { kind: "workspace", before: original, predecessors: [...this.effectBarriers], execute, reconcile };
    this.effectBarriers.push(barrier);
    const operation = this.tail.then(async () => {
      try { this.assertCurrent(); await this.finishQueued(); }
      catch (error) { barrier.needsResume = true; throw error; }
      await this.startEffect(barrier);
    });
    this.tail = operation.catch(() => undefined);
    return operation;
  }

  private async startWorkspaceEffect(barrier: WorkspaceEffectBarrier) {
    const before = reconcileEffectDraft(copy(barrier.before), barrier.predecessors);
    const raw = this.layout.payload;
    const layout = workspaceLayoutSchema.parse({ ...raw, componentsMap: typeof raw.componentsMap === "string" ? JSON.parse(raw.componentsMap) : raw.componentsMap });
    if (canonical(before.layout) !== canonical(layout) || before.components.some(row => canonical(row) !== canonical(this.components.get(row.id)?.payload))) {
      this.conflict = new WorkspaceSaveConflictError(null); throw this.conflict;
    }
    for (const ref of layout.componentsMap) if (!before.components.some(row => row.id === ref.id)) {
      this.conflict = new WorkspaceSaveConflictError(ref.id); throw this.conflict;
    }
    barrier.base = copy(before);
    // Previously restored or closed rows may remain cached for ordinary saves.
    // Only this captured workspace participates in the transaction baseline.
    barrier.expected = copy({ ownerId: this.identity.expectedOwnerId,
      components: before.components.map(row => this.components.get(row.id)!), layout: this.layout });
    barrier.needsResume = false; this.external = barrier;
    await this.finishWorkspaceEffect(barrier);
  }

  private async finishWorkspaceEffect(barrier: WorkspaceEffectBarrier) {
    let result: WorkspaceSnapshot;
    try { result = await barrier.execute(copy(barrier.expected!)); }
    catch (error) { if (error instanceof WorkspaceSaveConflictError && !(error instanceof WorkspaceRecoveryPausedError)) this.conflict = error; throw error; }
    this.assertCurrent();
    // One transaction can return the current layout plus rows closed since the
    // original read. Validate bounded pages against one identical layout token.
    const pages = [];
    if (new TextEncoder().encode(JSON.stringify(result)).byteLength > MAX_WORKSPACE_PERSISTENCE_BYTES
      || result.ownerId !== this.identity.expectedOwnerId || result.components.length > 1101
      || new Set(result.components.map(row => row.componentId)).size !== result.components.length) throw new Error("Invalid workspace effect reply");
    for (let index = 0; index < Math.max(1, result.components.length); index += 100) {
      const rows = result.components.slice(index, index + 100);
      const page = parseWorkspacePersistenceResult({ ...this.identity, mode: "inspect", componentIds: rows.map(row => row.componentId) }, { ...result, components: rows });
      if (!("layout" in page)) throw new Error("Invalid workspace effect reply");
      pages.push(page);
    }
    const saved = { ...pages[0], components: pages.flatMap(page => page.components) };
    barrier.result = copy(saved);
    try {
      // Detect overlap even if a caller's UI reconciliation ignores its drafts.
      for (const retained of this.retainedSaves) if (retained.barriers.includes(barrier)) {
        const predecessors = retained.barriers.slice(0, retained.barriers.indexOf(barrier));
        reconcileWorkspaceEffectDraft(barrier.base!, saved, reconcileEffectDraft(retained.draft, predecessors));
      }
      reconcileWorkspaceEffectDraft(barrier.base!, saved, barrier.base!);
    } catch (error) {
      if (error instanceof WorkspaceRecoveryPausedError) throw error;
      this.conflict = error instanceof WorkspaceSaveConflictError ? error : new WorkspaceSaveConflictError(null);
      throw this.conflict;
    }
    try {
      if (!barrier.reconcile(copy(saved), copy(barrier.base!))) throw new WorkspaceSaveConflictError(null);
    } catch (error) {
      if (error instanceof WorkspaceRecoveryPausedError) throw error;
      this.conflict = error instanceof WorkspaceSaveConflictError ? error : new WorkspaceSaveConflictError(null);
      barrier.reconciliationConflict = this.conflict; throw this.conflict;
    }
    this.assertCurrent();
    for (const row of saved.components) this.components.set(row.componentId, copy(row));
    this.layout = copy(saved.layout); this.external = null;
    this.effectBarriers = this.effectBarriers.filter(value => value !== barrier);
  }

  resumeWorkspaceEffect(execute: WorkspaceEffectBarrier["execute"], reconcile: WorkspaceEffectBarrier["reconcile"]): Promise<void> {
    const operation = this.tail.then(async () => {
      if (this.invalidated || !this.isCurrent()) throw new WorkspaceOwnerChangedError();
      if (this.recoveryPause) throw new WorkspaceRecoveryPausedError();
      const barrier = this.external;
      if (!barrier || barrier.kind !== "workspace" || barrier.execute !== execute) throw new Error("The original workspace transaction is unavailable");
      if (this.conflict && this.conflict !== barrier.reconciliationConflict) throw this.conflict;
      this.conflict = null; barrier.reconcile = reconcile;
      await this.finishWorkspaceEffect(barrier); await this.finishQueued();
    });
    this.tail = operation.catch(() => undefined); return operation;
  }

  private async startEffect(barrier: EffectBarrier) {
    try {
      this.assertCurrent();
      if (barrier.kind === "workspace") return await this.startWorkspaceEffect(barrier);
      const effectiveBefore = reconcileEffectDraft({ components: [barrier.before] }, barrier.predecessors).components.find(row => row.id === barrier.componentId);
      const baseline = this.components.get(barrier.componentId);
      if (!effectiveBefore || barrier.locallyChanged || !baseline?.fingerprint || canonical(baseline.payload) !== canonical(effectiveBefore)) {
        this.conflict = new WorkspaceSaveConflictError(barrier.componentId);
        throw this.conflict;
      }
      barrier.base = copy(effectiveBefore);
      barrier.expectedFingerprint = baseline.fingerprint;
      barrier.needsResume = false;
      this.external = barrier;
      await this.finishExternal();
    } catch (error) {
      // A conflict while transforming predecessor results must fence later
      // autosaves just like a conflict returned by persistence itself.
      if (error instanceof WorkspaceSaveConflictError && !(error instanceof WorkspaceRecoveryPausedError)) this.conflict = error;
      throw error;
    }
  }

  private async finishExternal() {
    const barrier = this.external;
    if (!barrier) return;
    this.assertCurrent();
    if (barrier.kind === "workspace") return this.finishWorkspaceEffect(barrier);
    let result: ComponentSnapshot;
    try {
      result = await barrier.execute(barrier.expectedFingerprint!);
    } catch (error) {
      if (error instanceof WorkspaceSaveConflictError && !(error instanceof WorkspaceRecoveryPausedError)) this.conflict = error;
      throw error;
    }
    this.assertCurrent();
    const checked = parseWorkspacePersistenceResult({ ...this.identity, mode: "inspect", componentIds: [barrier.componentId] }, {
      ownerId: this.identity.expectedOwnerId, components: [result], layout: this.layout,
    });
    if (!("layout" in checked)) throw new Error("Invalid component effect reply");
    const row = checked.components[0];
    barrier.result = copy(row);
    // A deleted target requires explicit reconciliation; never resurrect it here.
    try {
      if (barrier.locallyChanged) throw new WorkspaceSaveConflictError(barrier.componentId);
      if ((!row.payload || barrier.layoutChanged) && !barrier.recovering
        || !barrier.reconcile(copy(row), copy(barrier.base!))) throw new WorkspaceSaveConflictError(barrier.componentId);
    } catch {
      this.conflict = new WorkspaceSaveConflictError(barrier.componentId);
      if (!barrier.locallyChanged) barrier.reconciliationConflict = this.conflict;
      throw this.conflict;
    }
    this.assertCurrent();
    this.components.set(barrier.componentId, copy(row));
    this.external = null;
    this.effectBarriers = this.effectBarriers.filter(value => value !== barrier);
  }

  /** Adopt only rows applied synchronously from this exact read. Layout authority stays unchanged. */
  restore(componentIds: readonly string[], apply: (components: WorkspaceSnapshot["components"]) => string[]): Promise<void> {
    const ids = [...componentIds];
    const operation = this.tail.then(async () => {
      this.assertCurrent();
      await this.finishQueued();
      if (ids.length > 1000 || new Set(ids).size !== ids.length) throw new Error("Invalid workspace restore IDs");
      const rows: WorkspaceSnapshot["components"] = [];
      for (let index = 0; index < ids.length; index += 100) {
        this.assertCurrent();
        const input = workspacePersistenceRequestSchema.parse({ ...this.identity, mode: "inspect", componentIds: ids.slice(index, index + 100) });
        const page = parseWorkspacePersistenceResult(input, await this.transport(input));
        this.assertCurrent();
        if (!("layout" in page)) throw new Error("Invalid workspace restore snapshot");
        rows.push(...page.components);
      }
      this.assertCurrent();
      const applied = apply(copy(rows));
      this.assertCurrent();
      if (!Array.isArray(applied) || new Set(applied).size !== applied.length || applied.some(id => !rows.some(row => row.componentId === id && row.payload !== null))) {
        throw new Error("Invalid workspace restore acknowledgement");
      }
      for (const row of rows) if (applied.includes(row.componentId)) this.components.set(row.componentId, copy(row));
    });
    this.tail = operation.catch(() => undefined);
    return operation;
  }

  retryPending(): Promise<void> {
    const operation = this.tail.then(async () => {
      this.assertCurrent();
      await this.finishQueued();
    });
    this.tail = operation.catch(() => undefined);
    return operation;
  }

  /** Only the holder of the original execute callback can recover its placement. */
  resumeEffect(execute: ComponentEffectBarrier["execute"], reconcile: ComponentEffectBarrier["reconcile"]): Promise<void> {
    const operation = this.tail.then(async () => {
      if (this.invalidated || !this.isCurrent()) throw new WorkspaceOwnerChangedError();
      if (this.recoveryPause) throw new WorkspaceRecoveryPausedError();
      const barrier = this.external;
      if (!barrier || barrier.kind !== "component" || barrier.execute !== execute) throw new Error("The original workspace effect is unavailable");
      if (barrier.locallyChanged || this.conflict && this.conflict !== barrier.reconciliationConflict) {
        throw this.conflict ?? new WorkspaceSaveConflictError(barrier.componentId);
      }
      this.conflict = null;
      barrier.recovering = true;
      barrier.reconcile = reconcile;
      await this.finishExternal();
      await this.finishQueued();
    });
    this.tail = operation.catch(() => undefined);
    return operation;
  }

  private assertCurrent() {
    if (this.invalidated || !this.isCurrent()) throw new WorkspaceOwnerChangedError();
    if (this.recoveryPause) throw new WorkspaceRecoveryPausedError();
    if (this.conflict) throw this.conflict;
  }

  /** Serial execution retains every submitted edit while responses are pending. */
  save(draft: WorkspaceSaveDraft, options: { preserveMissingLayoutReferences?: boolean } = {}): Promise<void> {
    const preserveMissing = options.preserveMissingLayoutReferences === true;
    let frozen: WorkspaceSaveDraft;
    try { frozen = draftSchema.parse(copy(draft)); } catch (error) { return Promise.reject(error); }
    const submission = ++this.recoverySubmission;
    for (const row of frozen.components) this.recoveryTouchedIds.set(row.id, submission);
    const barriers = [...this.effectBarriers];
    for (const barrier of barriers) {
      if (barrier.kind === "workspace") continue;
      const target = frozen.components.find(value => value.id === barrier.componentId);
      const previous = [...barrier.predecessors].reverse().find((value): value is ComponentEffectBarrier => value.kind === "component" && value.componentId === barrier.componentId && !!value.result?.payload);
      const expected = barrier.result?.payload ?? previous?.result?.payload ?? barrier.before;
      const closed = frozen.layout && !frozen.layout.componentsMap.some(value => value.id === barrier.componentId);
      if (target && canonical(target) !== canonical(expected) && !(closed && sameClosedContent(target, expected))) barrier.locallyChanged = true;
      if (closed) barrier.layoutChanged = true;
    }
    const retained = barriers.length ? { draft: frozen, preserveMissing, barriers, failed: false } : null;
    if (retained) this.retainedSaves.push(retained);
    const operation = this.tail.then(async () => {
      try {
        this.assertCurrent();
        // Resolve uncertain older writes before advancing a captured draft.
        await this.finishQueued();
        this.assertCurrent();
        await this.applyDraft(frozen, preserveMissing, barriers);
        for (const row of frozen.components) if (this.recoveryTouchedIds.get(row.id) === submission) this.recoveryTouchedIds.delete(row.id);
        if (retained) this.retainedSaves = this.retainedSaves.filter(value => value !== retained);
      } catch (error) {
        if (retained) {
          if (this.external || this.conflict || this.pending || this.plan || this.effectBarriers.some(value => value.needsResume)) retained.failed = true;
          else this.retainedSaves = this.retainedSaves.filter(value => value !== retained);
        }
        throw error;
      }
    });
    this.tail = operation.catch(() => undefined);
    return operation;
  }

  private async applyDraft(frozen: WorkspaceSaveDraft, preserveMissing: boolean, barriers: EffectBarrier[], resumeEffects = true) {
    const draft = reconcileEffectDraft(frozen, barriers);
    this.plan = { draft, preserveMissing };
    await this.finishPlan(resumeEffects);
  }

  private async finishQueued() {
    await this.finishPlan(false);
    await this.finishRetainedSaves();
    await this.finishPlan();
  }

  private async finishRetainedSaves() {
    for (const saved of [...this.retainedSaves]) {
      if (!saved.failed) continue;
      this.assertCurrent();
      await this.finishPlan(false);
      // Effects captured before this save must run before it; later effects wait.
      for (const barrier of saved.barriers) if (barrier.needsResume) await this.startEffect(barrier);
      try {
        await this.applyDraft(saved.draft, saved.preserveMissing, saved.barriers, false);
      } catch (error) {
        if (!this.pending && !this.plan && !this.external && !this.conflict) this.retainedSaves = this.retainedSaves.filter(value => value !== saved);
        throw error;
      }
      this.retainedSaves = this.retainedSaves.filter(value => value !== saved);
    }
  }

  private async finishPlan(resumeEffects = true) {
    await this.finishExternal();
    if (this.pending) await this.commit(this.pending);
    while (this.plan) {
      this.assertCurrent();
      let input: SaveRequest;
      try {
        const { draft: frozen, preserveMissing } = this.plan;
        const remaining = frozen.components.filter(value => canonical(value) !== canonical(this.components.get(value.id)?.payload ?? null))
          .map(payload => ({ componentId: payload.id, expectedFingerprint: this.components.get(payload.id)?.fingerprint ?? null, payload }));
        let layout = frozen.layout;
        if (layout && preserveMissing) {
          const raw = this.layout.payload.componentsMap;
          const existing = workspaceLayoutSchema.parse({ ...this.layout.payload, componentsMap: typeof raw === "string" ? JSON.parse(raw) : raw });
          layout = { ...layout, componentsMap: Array.from(new Map([...existing.componentsMap, ...layout.componentsMap].map(value => [value.id, value])).values()) };
        }
        const layoutChanged = layout !== undefined && canonical(layout) !== canonical(this.layout.payload);
        if (remaining.length === 0 && !layoutChanged && !this.recoveryValidationPending) { this.plan = null; break; }
        const raw = this.layout.payload.componentsMap;
        const existingLayout = workspaceLayoutSchema.parse({ ...this.layout.payload, componentsMap: typeof raw === "string" ? JSON.parse(raw) : raw });
        let count = Math.min(remaining.length, 100);
        for (;;) {
          const changes = remaining.slice(0, count);
          const creating = changes.some(change => change.expectedFingerprint === null);
          const finalBatch = count === remaining.length;
          const parsed = workspacePersistenceRequestSchema.parse({ ...this.identity, mode: "save", changes,
            ...(this.recoveryGuard ? { recovery: this.recoveryGuard } : {}),
            ...(this.recoveryGuard || creating || finalBatch && layoutChanged ? { layout: { expectedFingerprint: this.layout.fingerprint,
              payload: finalBatch && layout ? layout : existingLayout } } : {}),
          });
          if (parsed.mode !== "save") throw new Error("Invalid workspace save");
          input = parsed;
          if (new TextEncoder().encode(JSON.stringify(input)).byteLength <= MAX_WORKSPACE_PERSISTENCE_BYTES) break;
          if (count <= 1) { this.plan = null; throw new Error("Workspace component or layout is too large to save"); }
          count -= 1;
        }
      } catch (error) {
        this.plan = null;
        throw error;
      }
      this.pending = input;
      this.pendingUncertain = false;
      await this.commit(input);
    }
    // Failed predecessor persistence can abort an effect before dispatch. Resume
    // that registered operation after the old save plan, before later drafts.
    for (const barrier of resumeEffects ? [...this.effectBarriers] : []) {
      if (barrier.needsResume) await this.startEffect(barrier);
    }
  }

  private async commit(input: SaveRequest) {
    this.assertCurrent();
    let receipt: WorkspacePersistenceResult;
    try {
      const response = await this.transport(copy(input));
      this.assertCurrent();
      receipt = parseWorkspacePersistenceResult(input, response);
    } catch (error) {
      if (error instanceof WorkspaceRequestRejectedError && !this.pendingUncertain) {
        this.pending = null;
        this.plan = null;
      } else {
        // A rejected retry cannot prove an earlier ambiguous attempt did not commit.
        this.pendingUncertain = true;
      }
      throw error;
    }
    if (!("outcome" in receipt)) throw new Error("Invalid workspace save receipt");
    if (receipt.outcome === "conflict") {
      this.conflict = new WorkspaceSaveConflictError(receipt.componentId);
      throw this.conflict;
    }
    // All receipt fields are verified before advancing any baseline.
    for (let index = 0; index < input.changes.length; index += 1) {
      const change = input.changes[index];
      this.components.set(change.componentId, { componentId: change.componentId,
        payload: copy(change.payload), fingerprint: receipt.components[index].fingerprint });
    }
    if (input.layout) this.layout = { payload: copy(input.layout.payload), fingerprint: receipt.layoutFingerprint! };
    if (input.recovery) this.recoveryValidationPending = false;
    this.pending = null;
    this.pendingUncertain = false;
  }
}
